import { queryOptions } from "@tanstack/react-query";
import type {
  HomeTestimonialCard,
  PublicTestimonial,
  TestimonialPlacement,
} from "@/types/testimonials";
import { apiUrl } from "@/lib/api-url";

/** `home` gets lean marquee cards, `therapists` the full public shape. */
type TestimonialFor<P extends TestimonialPlacement> = P extends "home"
  ? HomeTestimonialCard
  : PublicTestimonial;

export interface TestimonialsEnvelope<P extends TestimonialPlacement> {
  testimonials: TestimonialFor<P>[];
}

/**
 * SSR-safe public query for the curated testimonials of one placement
 * ("home" = homepage marquee, "therapists" = /za-terapeute carousel).
 * apiUrl() resolves to loopback server-side (a bare relative fetch throws under
 * Bun); the placement is part of the key so SSR invalidation only drops the
 * pages that actually render it.
 */
export function testimonialsQuery<P extends TestimonialPlacement>(placement: P) {
  return queryOptions({
    queryKey: ["homepage-testimonials", "public", placement] as const,
    queryFn: async (): Promise<TestimonialsEnvelope<P>> => {
      const res = await fetch(apiUrl(`/api/homepage-testimonials?placement=${placement}`));
      if (!res.ok) throw new Error("Failed to fetch testimonials");
      return res.json();
    },
  });
}

export const homepageTestimonialsQuery = testimonialsQuery("home");
export const therapistTestimonialsQuery = testimonialsQuery("therapists");
